// GexPhaseDeltaFlow — Net GEX over time with dedicated Delta Flow overlay support.
//
// The literal GEX Phase visualization (see [[gex-regime-vs-phase]]): a time series
// of Net GEX with the Neutral band shaded. The line riding above the band =
// Positive phase (pin), inside = Neutral, below = Negative (trend).
//
// Source = greeks-feed gamma net_exposure (gammaNetExposureSeries) — the canonical
// Net GEX the MISD zero_gex lifecycle thresholds (±500 suitability / ±1000
// expansion) and the SPX Total GEX chart study read. NOT the chain-sum aggregate,
// which is session-cumulative flow and cannot round-trip intraday.
//
//This version overlays net delta bubbles over the GEX chart to help identify
// when delta flow is supportive of the 0 GEX trade.

import React, { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { WidgetPanelControls, WidgetPanelLiveStatus } from '../Dashboard/WidgetPanelSlot';
import useGreeksWebsocketDataStore from '../../Store/GreeksWebsocketDataStore';
import usePlaybackStore from '../../Store/playbackStore';
import useWidgetMinuteFocusStore, {
  useFocusedMinuteSourceCleanup,
} from '../../Store/widgetMinuteFocusStore';
import { isDuringMarketHours, latestTradeDayOnly } from '../../Utils/marketHours';
import { focusedPointIndex } from '../../Utils/widgetMinuteFocus';
import {
  denseSeriesGapRanges,
  forwardFillSeriesAtGaps,
  mergeSeriesGapRanges,
  replaySeriesGapRanges,
} from '../../Utils/seriesPresentation';
import {
  GEX_NEUTRAL_BAND,
  PHASE_META,
  gammaNetExposureSeries,
  gexPhase,
} from '../../Utils/zeroGex';
import { selectVolumeDots } from '../../Utils/strikeFlow';
import { PLOT_PAN_THRESHOLD_PX } from '../../Utils/chartTimeWindow';
import { gexTimeAxisTicks, zoomGexTimeRange } from '../../Utils/gexTimeWindow';
import {
  bubbleCanvasEncoding,
  getBookmapBubbleCandidatesSnapshot,
  subscribeBookmapBubbleCandidates,
} from './bookmapPrimitives';
import {
  VOLUME_DOT_THRESHOLD_OPTIONS,
  formatVolumeThreshold,
  useBookmapSettingsStore,
} from '../../Store/bookmapSettingsStore';
import useGexSharedTimeWindow from './useGexSharedTimeWindow';
import useNonPassiveWheel from './useNonPassiveWheel';
import '../../Styles/GexPhaseDeltaFlow.css';

const ML = 6, MR = 34, MT = 6, MB = 14; // MR: y-axis labels (~24px text + gap); MB: time tick (~8px + 5px offset)
const GEX_REFERENCE_LEVEL = 1000;
const MIN_AXIS_LABEL_GAP_PX = 9;

// Local linear scale (same pattern as FlowGexChart).
const scaleLinear = ([d0, d1], [r0, r1]) => (v) =>
  d1 === d0 ? r0 : r0 + ((v - d0) / (d1 - d0)) * (r1 - r0);

const clamp = (value, min, max) =>
  Math.max(min, Math.min(max, value));

const fmtGex = (v) => {
  const a = Math.abs(v);
  const s = v < 0 ? '-' : '+';
  return a >= 1000 ? `${s}${(a / 1000).toFixed(1)}k` : `${s}${Math.round(a)}`;
};

const fmtAxisTime = (epochMs) => {
  try {
    return new Date(epochMs).toLocaleTimeString('en-US', {
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: 'America/New_York',
    });
  } catch { return ''; }
};

const MIN_TIME_LABEL_GAP_PX = 46;

function selectSpacedTimeTicks(
  candidates,
  xFn,
  minGap = MIN_TIME_LABEL_GAP_PX,
) {
  const sorted = [...new Set(candidates)].sort((a, b) => a - b);

  if (sorted.length <= 2) return sorted;

  const first = sorted[0];
  const last = sorted[sorted.length - 1];

  const selected = [first];

  for (let i = 1; i < sorted.length - 1; i += 1) {
    const index = sorted[i];

    const previousX = xFn(selected[selected.length - 1]);
    const currentX = xFn(index);
    const lastX = xFn(last);

    if (
      currentX - previousX >= minGap
      && lastX - currentX >= minGap
    ) {
      selected.push(index);
    }
  }

  if (last !== first) {
    selected.push(last);
  }

  return selected;
}

const minuteBucketMs = (t) => {
  const n = Number(t);
  return Number.isFinite(n) ? Math.floor(n / 60000) * 60000 : null;
};

function flowmapBubbleBackground(bubble) {
  const encoding = bubbleCanvasEncoding(bubble);

  // Estimated bubbles already carry the exact muted fill/hatch colors used by
  // the Flowmap canvas renderer.
  if (encoding.kind === 'estimated') {
    const style = encoding.ambiguousStyle;
    return `repeating-linear-gradient(
      135deg,
      ${style.hatchStyle} 0 1.5px,
      transparent 1.5px 6px
    ), ${style.fillStyle}`;
  }

  // Flowmap neutral fill.
  if (encoding.kind === 'neutral') {
    return 'rgba(140,148,160,0.8)';
  }

  // These are the literal bullish/bearish fills used by _drawBubbles().
  const baseColor =
    encoding.baseTone === 'bullish'
      ? 'rgb(0,191,58)'
      : 'rgb(208,0,0)';

  const share = Math.max(
    0,
    Math.min(1, Number(bubble.share) || 0),
  );
  const minorityShare = Math.min(share, 1 - share);

  if (minorityShare <= 0) {
    return baseColor;
  }

  // Match the Flowmap's dark counter-Delta wedge.
  const counterDeg = minorityShare * 360;
  const counterCenter =
    encoding.baseTone === 'bullish'
      ? 0
      : 180;
  const counterStart =
    counterCenter - counterDeg / 2;

  return `conic-gradient(
    from ${counterStart}deg,
    rgba(3,7,18,0.58) 0deg ${counterDeg}deg,
    transparent ${counterDeg}deg 360deg
  ), ${baseColor}`;
}

function nearestSeriesIndex(series, targetTime, maxDistanceMs = 90000) {
  if (!series.length || !Number.isFinite(targetTime)) return null;

  let lo = 0;
  let hi = series.length - 1;

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (Number(series[mid]?.t) < targetTime) lo = mid + 1;
    else hi = mid;
  }

  const candidates = [lo, lo - 1]
    .filter((idx) => idx >= 0 && idx < series.length);

  let bestIdx = null;
  let bestDistance = Infinity;

  for (const idx of candidates) {
    const distance = Math.abs(Number(series[idx]?.t) - targetTime);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestIdx = idx;
    }
  }

  return bestDistance <= maxDistanceMs ? bestIdx : null;
}

export function seriesIndexAtPlotX(series, x, plotLeft, plotRight, domainStart, domainEnd) {
  const fraction = clamp((x - plotLeft) / Math.max(1, plotRight - plotLeft), 0, 1);
  const targetTime = domainStart + fraction * (domainEnd - domainStart);
  return nearestSeriesIndex(series, targetTime, Infinity);
}

export default function GexPhaseDeltaFlow() {
  // The WS initial payload backfills greek_exposures for the session, so the
  // strip shows full-session history on load; updates append per feed tick.
  const exposures = useGreeksWebsocketDataStore((s) => s.greekExposures?.spx);
  const liveGreekGaps = useGreeksWebsocketDataStore((s) => s.liveGapRanges || []);
  const replaying = usePlaybackStore((state) => state.mode === 'playing');
  const replayCoverage = usePlaybackStore((state) => state.replayCoverage);
  const replayCutoffMs = usePlaybackStore((state) => state.replayCutoffMs);

  // Subscribe to the exact pre-threshold +/-25-point bubble candidates built
  // by Flowmap. This widget applies only its own threshold/sizing pass.
  const flowmapBubbleCandidates = useSyncExternalStore(
    subscribeBookmapBubbleCandidates,
    getBookmapBubbleCandidatesSnapshot,
    getBookmapBubbleCandidatesSnapshot,
  );

  const gexDeltaFlowMinVolume = useBookmapSettingsStore(
    (s) => s.gexDeltaFlowMinVolume,
  );
  const setGexDeltaFlowMinVolume = useBookmapSettingsStore(
    (s) => s.setGexDeltaFlowMinVolume,
  );

  const gexFlowBubbles = useMemo(
    () => selectVolumeDots(
      flowmapBubbleCandidates,
      gexDeltaFlowMinVolume,
    ),
    [
      flowmapBubbleCandidates,
      gexDeltaFlowMinVolume,
    ],
  );

  const [size, setSize] = useState({ w: 320, h: 120 });
  const [hover, setHover] = useState(null); // { idx, t, v } | null
  const [viewRange, setViewRange] = useState(null); // null = full session
  const [isPanning, setIsPanning] = useState(false);
  const focusedMinuteMs = useWidgetMinuteFocusStore((state) => state.minuteMs);
  const focusSourceId = useWidgetMinuteFocusStore((state) => state.sourceId);
  const setFocusedMinute = useWidgetMinuteFocusStore((state) => state.setFocusedMinute);
  const clearFocusedMinute = useWidgetMinuteFocusStore((state) => state.clearFocusedMinute);
  useFocusedMinuteSourceCleanup('gexPhaseDeltaFlow');
  const panRef = useRef(null);
  const viewDetachedRef = useRef(false);

  // Callback ref (not a mount-once effect): the component can mount in the
  // empty state, where a run-once observer would attach to nothing and the
  // chart would stay at the default 320x120 once data arrived. The callback
  // re-attaches whenever the wrap node (re)appears.
  const roRef = useRef(null);
  const wrapRef = useCallback((node) => {
    if (roRef.current) { roRef.current.disconnect(); roRef.current = null; }
    if (!node) return;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setSize({ w: Math.max(1, Math.round(width)), h: Math.max(1, Math.round(height)) });
    });
    ro.observe(node);
    roRef.current = ro;
  }, []);

  // Single-trade-day display: full prior session after hours / pre-market,
  // resets to current-day-only once today's first in-hours data arrives.
  const observedSeries = useMemo(
    () => latestTradeDayOnly(
      gammaNetExposureSeries(exposures).filter((d) => isDuringMarketHours(d.t)),
    ),
    [exposures],
  );

  const gaps = useMemo(() => mergeSeriesGapRanges(
    replaying
      ? replaySeriesGapRanges(
          replayCoverage,
          ['greeks:exposures'],
          replayCutoffMs,
        )
      : liveGreekGaps,
    denseSeriesGapRanges(observedSeries, { source: 'greeks:exposures' }),
  ), [liveGreekGaps, observedSeries, replaying, replayCoverage, replayCutoffMs]);
  const series = useMemo(
    () => forwardFillSeriesAtGaps(observedSeries, gaps),
    [gaps, observedSeries],
  );
  const {
    domainMs,
    minSpanMs,
    publishSession,
    schedulePublish,
  } = useGexSharedTimeWindow({
    series,
    sourceId: 'gex-phase-delta-flow',
    setViewRange,
    viewDetachedRef,
  });

  const wheelHandlerRef = useRef(null);
  const chartWheelRef = useNonPassiveWheel(wheelHandlerRef);
  const zoomBaseRef = useRef(null);

  // Underlying bubble candidates come directly from Flowmap. Only the
  // GEX-specific threshold and radius scaling are applied here.

  const latest = series.length ? series[series.length - 1] : null;
  const current = latest?.v ?? null;
  const phase = current == null ? null : gexPhase(current);
  const meta = phase ? PHASE_META[phase] : null;

  if (!series.length) {
    return (
      <div className="gpdf-panel">
        {/* keep the measured wrap mounted so size is right when data arrives */}
        <div className="gpdf-wrap" ref={wrapRef}>
          <div className="gpdf-empty" style={{ height: '100%' }}>Loading...</div>
        </div>
      </div>
    );
  }

  const { w: VW, h: VH } = size;
  const n = series.length;
  const fullStart = series[0].t;
  const fullEnd = series[n - 1].t;

  // A null viewRange means "follow the entire session". Once the user zooms,
  // the range becomes fixed so new live points do not pull the historical
  // inspection window away from them.
  let viewStart = viewRange?.start ?? fullStart;
  let viewEnd = viewRange?.end ?? fullEnd;

  const domainStart = domainMs?.from ?? fullStart;
  const domainEnd = domainMs?.to ?? fullEnd;
  viewStart = clamp(viewStart, domainStart, domainEnd);
  viewEnd = clamp(viewEnd, viewStart, domainEnd);

  if (fullEnd > fullStart && viewEnd - viewStart < 60_000) {
    viewEnd = Math.min(fullEnd, viewStart + 60_000);
    viewStart = Math.max(fullStart, viewEnd - 60_000);
  }

  // Every gesture composes on this rather than on the rendered range. A
  // trackpad delivers a burst of wheel events inside a single task, and all of
  // them would otherwise start from the same pre-burst range, so eight events
  // of input produced one event's worth of zoom.
  zoomBaseRef.current = { from: viewStart, to: viewEnd };

  const isZoomed =
    viewRange != null
    && (
      viewStart > fullStart + 0.01
      || viewEnd < fullEnd - 0.01
    );

  // The window is clamped to the session close, not to the last point, so it
  // can sit entirely outside the data. Clamping the missing index to 0 made the
  // whole series "visible": the polyline drew off-plot and the y-axis
  // autoscaled to the full session behind an empty plot.
  const firstInWindow = series.findIndex((point) => point.t >= viewStart);
  let lastInWindow = firstInWindow < 0 ? -1 : n - 1;
  while (lastInWindow >= 0 && series[lastInWindow].t > viewEnd) {
    lastInWindow -= 1;
  }
  const hasVisiblePoints = firstInWindow >= 0 && lastInWindow >= firstInWindow;
  const visibleStartIdx = hasVisiblePoints ? firstInWindow : 0;
  const visibleEndIdx = hasVisiblePoints ? lastInWindow : -1;
  const visibleSeries = hasVisiblePoints
    ? series.slice(visibleStartIdx, visibleEndIdx + 1)
    : [];
  const xTimeFn = scaleLinear(
    [viewStart, Math.max(viewStart + 60_000, viewEnd)],
    [ML, VW - MR],
  );

  // Y auto-scales to the visible time window, but always preserves the
  // strategically meaningful +/-500 and +/-1000 GEX reference structure.
  const vals = visibleSeries.map((d) => d.v);
  const lo = Math.min(-GEX_REFERENCE_LEVEL, -GEX_NEUTRAL_BAND, ...vals);
  const hi = Math.max(GEX_REFERENCE_LEVEL, GEX_NEUTRAL_BAND, ...vals);
  const pad = (hi - lo) * 0.12 || 1;
  const yFn = scaleLinear([lo - pad, hi + pad], [VH - MB, MT]);

  const yZero = yFn(0);
  const yRefPos = yFn(GEX_REFERENCE_LEVEL);
  const yRefNeg = yFn(-GEX_REFERENCE_LEVEL);
  const yPos  = yFn(GEX_NEUTRAL_BAND);
  const yNeg  = yFn(-GEX_NEUTRAL_BAND);
  const showPosBandLabel = Math.abs(yRefPos - yPos) >= MIN_AXIS_LABEL_GAP_PX;
  const showNegBandLabel = Math.abs(yRefNeg - yNeg) >= MIN_AXIS_LABEL_GAP_PX;

  // Put each Delta bubble directly on the Net GEX line at the matching minute.
  const deltaBubbles = gexFlowBubbles
    .map((dot) => {
      const bucket = minuteBucketMs(dot.t);
      const idx = nearestSeriesIndex(series, bucket);
      if (idx == null) return null;
      if (series[idx].t < viewStart || series[idx].t > viewEnd) return null;

      return {
        ...dot,
        gexIdx: idx,
        gexValue: series[idx].v,
        cx: xTimeFn(series[idx].t),
        cy: yFn(series[idx].v),
      };
    })
    .filter(Boolean);

  const synchronizedIndex = focusSourceId !== 'gexPhaseDeltaFlow'
    ? focusedPointIndex(series, focusedMinuteMs, visibleStartIdx, visibleEndIdx)
    : null;
  const activeHover = hover ?? (synchronizedIndex == null ? null : {
    idx: synchronizedIndex,
    t: series[synchronizedIndex].t,
    v: series[synchronizedIndex].v,
  });

  // Hover-derived display values (fall back to current when not hovering).
  const hoverMeta   = hover ? PHASE_META[gexPhase(hover.v)] : null;
  const displayMeta = hoverMeta ?? meta ?? { label: 'Unavailable', color: '#f1d36a' };
  const displayVal  = hover != null ? hover.v : current;
  const hoverX = activeHover ? xTimeFn(activeHover.t) : null;
  const hoverY = activeHover ? yFn(activeHover.v) : null;

  const resetZoom = () => {
    publishSession();
    setHover(null);
    clearFocusedMinute('gexPhaseDeltaFlow');
    panRef.current = null;
    setIsPanning(false);
  };

  const handlePointerMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const plotWidth = Math.max(1, VW - MR - ML);

    const pan = panRef.current;

    if (pan) {
      const dx = e.clientX - pan.startX;
      if (!pan.active && Math.abs(dx) < PLOT_PAN_THRESHOLD_PX) return;
      if (!pan.active) {
        pan.active = true;
        setIsPanning(true);
      }
      const span = pan.startRange.end - pan.startRange.start;
      const shift = -(dx / plotWidth) * span;
      // Local until the store accepts the publish below. Replay stores the
      // pinned position for its trading date and preserves it across scrubs.
      viewDetachedRef.current = true;

      let nextStart = pan.startRange.start + shift;
      let nextEnd = pan.startRange.end + shift;

      if (nextStart < domainStart) {
        nextEnd += domainStart - nextStart;
        nextStart = domainStart;
      }

      if (nextEnd > domainEnd) {
        nextStart -= nextEnd - domainEnd;
        nextEnd = domainEnd;
      }

      const panned = {
        from: Math.max(domainStart, nextStart),
        to: Math.min(domainEnd, nextEnd),
      };
      zoomBaseRef.current = panned;
      setViewRange({ start: panned.from, end: panned.to });
      // `positionIsIntent` keeps an interior pan absolute. Without it the
      // classifier reads the range as a duration and re-anchors it to each
      // chart's own data edge, which moves them somewhere nobody dragged.
      schedulePublish(panned, { positionIsIntent: true });
    }

    // A window holding no point has nothing under the cursor. The index lookup
    // takes no distance tolerance, so it would snap to the nearest bar outside
    // the window and publish that minute to every synchronized widget.
    if (!hasVisiblePoints || mx < ML || mx > VW - MR) {
      setHover(null);
      clearFocusedMinute('gexPhaseDeltaFlow');
      return;
    }

    const idx = seriesIndexAtPlotX(series, mx, ML, VW - MR, viewStart, viewEnd);

    setHover({
      idx,
      t: series[idx].t,
      v: series[idx].v,
    });
    setFocusedMinute('gexPhaseDeltaFlow', series[idx].t);
  };

  const handlePointerDown = (e) => {
    if (e.button !== 0 || n <= 1) return;

    e.currentTarget.setPointerCapture?.(
      e.pointerId,
    );

    panRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startRange: {
        start: viewStart,
        end: viewEnd,
      },
      active: false,
    };
  };

  const finishPan = (e) => {
    if (
      panRef.current
      && (
        e?.pointerId == null
        || e.pointerId === panRef.current.pointerId
      )
    ) {
      if (
        e?.currentTarget
        && e.pointerId != null
      ) {
        e.currentTarget.releasePointerCapture?.(
          e.pointerId,
        );
      }

      panRef.current = null;
      setIsPanning(false);
    }
  };

  const handleWheel = (e) => {
    // Claim the gesture before data and delta checks. The plot exists with one
    // or two rows, and the dashboard must not scroll while the pointer is on it.
    e.preventDefault();
    if (n <= 2 || !domainMs) return;
    if (e.deltaY === 0) return;

    const plotWidth = Math.max(1, VW - MR - ML);
    const range = zoomBaseRef.current;

    const rect = e.currentTarget.getBoundingClientRect();
    const mx = clamp(
      e.clientX - rect.left,
      ML,
      VW - MR,
    );
    const anchorFrac = clamp(
      (mx - ML) / plotWidth,
      0,
      1,
    );

    const nextRange = zoomGexTimeRange({
      range,
      domain: domainMs,
      deltaY: e.deltaY,
      deltaMode: e.deltaMode,
      plotWidth,
      anchorFraction: anchorFrac,
      minSpan: minSpanMs,
    });
    if (!nextRange) return;

    viewDetachedRef.current = false;
    zoomBaseRef.current = nextRange;
    setViewRange({ start: nextRange.from, end: nextRange.to });
    schedulePublish(nextRange);
  };


  // Assigned here rather than passed to the hook: the handler needs the
  // resolved view range, which only exists past the empty-series return.
  wheelHandlerRef.current = handleWheel;

  return (
    <div className="gpdf-panel">
      <WidgetPanelLiveStatus />
      <WidgetPanelControls>
        <span className="gpdf-phase" style={{ color: displayMeta.color }}>
          {displayMeta.label.toUpperCase()}
        </span>
        <span className="gpdf-val" style={{ color: displayMeta.color }}>
          {displayVal == null ? '--' : fmtGex(displayVal)}
        </span>
        {hover && (
          <span className="gpdf-hover-time">{fmtAxisTime(hover.t)}</span>
        )}

        <label className="gpdf-threshold-control">
          <span>Vol ≥</span>
          <select
            value={gexDeltaFlowMinVolume}
            aria-label="GEX Delta Flow bubble volume threshold"
            onChange={(event) => {
              setGexDeltaFlowMinVolume(
                Number(event.target.value),
              );
            }}
          >
            {VOLUME_DOT_THRESHOLD_OPTIONS.map((value) => (
              <option key={value} value={value}>
                {formatVolumeThreshold(value)}
              </option>
            ))}
          </select>
        </label>

        {isZoomed && (
          <button
            type="button"
            className="gpdf-reset-zoom"
            onClick={resetZoom}
            title="Reset chart zoom"
          >
            Reset
          </button>
        )}

      </WidgetPanelControls>
      <div className="gpdf-wrap" ref={wrapRef}>
        <svg
          width={VW}
          height={VH}
          className={isPanning ? 'gpdf-chart is-panning' : 'gpdf-chart'}
          ref={chartWheelRef}
          onPointerMove={handlePointerMove}
          onPointerDown={handlePointerDown}
          onPointerUp={finishPan}
          onPointerCancel={finishPan}
          onPointerLeave={(e) => {
            if (!panRef.current) {
              setHover(null);
              clearFocusedMinute('gexPhaseDeltaFlow');
            }
            if (
              panRef.current
              && e.buttons === 0
            ) {
              finishPan(e);
            }
          }}
          onDoubleClick={resetZoom}
        >
          {/* Neutral ±band */}
          <rect
            className="gpdf-band"
            x={ML}
            y={yPos}
            width={Math.max(0, VW - MR - ML)}
            height={Math.max(0, yNeg - yPos)}
          />
          <line className="gpdf-zero" x1={ML} x2={VW - MR} y1={yZero} y2={yZero} />
          <line className="gpdf-ref" x1={ML} x2={VW - MR} y1={yRefPos} y2={yRefPos} />
          <line className="gpdf-ref" x1={ML} x2={VW - MR} y1={yRefNeg} y2={yRefNeg} />
          <line className="gpdf-edge" x1={ML} x2={VW - MR} y1={yPos}  y2={yPos}  />
          <line className="gpdf-edge" x1={ML} x2={VW - MR} y1={yNeg}  y2={yNeg}  />

          {/* net-GEX line — its position vs the band IS the phase history */}
          <polyline
            className="gpdf-line"
            points={visibleSeries.map((point) => `${xTimeFn(point.t)},${yFn(point.v)}`).join(' ')}
            fill="none"
            strokeWidth="1.5"
          />
          {/* current-value dot, colored by current phase */}
          {current != null && series[n - 1].t >= viewStart && series[n - 1].t <= viewEnd && (
            <circle cx={xTimeFn(series[n - 1].t)} cy={yFn(current)} r="2.6" fill={meta.color} />
          )}

          {/* y-axis band labels */}
          <text className="gpdf-axis gpdf-axis-ref" x={VW - MR + 4} y={yRefPos + 3}>+{GEX_REFERENCE_LEVEL}</text>
          {showPosBandLabel && (
            <text className="gpdf-axis" x={VW - MR + 4} y={yPos + 3}>+{GEX_NEUTRAL_BAND}</text>
          )}
          <text className="gpdf-axis" x={VW - MR + 4} y={yZero + 3}>0</text>
          {showNegBandLabel && (
            <text className="gpdf-axis" x={VW - MR + 4} y={yNeg + 3}>-{GEX_NEUTRAL_BAND}</text>
          )}
          <text className="gpdf-axis gpdf-axis-ref" x={VW - MR + 4} y={yRefNeg + 3}>-{GEX_REFERENCE_LEVEL}</text>

          {/* x-axis time labels: quarter-hours inside the window + its edges.
              Taken from the window, not the series, so a window that reserves
              the empty future is still labelled. */}
          {(() => {
            const all = selectSpacedTimeTicks(
              gexTimeAxisTicks(viewStart, viewEnd),
              (t) => xTimeFn(t),
            );

            return all.map((t, position) => (
              <text
                key={t}
                className="gpdf-axis"
                x={xTimeFn(t)}
                y={VH - 5}
                textAnchor={
                  position === 0
                    ? 'start'
                    : position === all.length - 1
                      ? 'end'
                      : 'middle'
                }
              >
                {fmtAxisTime(t)}
              </text>
            ));
          })()}

          {/* Crosshair */}
          {activeHover && (
            <>
              <line className="gpdf-crosshair" x1={hoverX} x2={hoverX} y1={MT}  y2={VH - MB} />
              {hover && (
                <>
                  <line className="gpdf-crosshair" x1={ML} x2={VW - MR} y1={hoverY} y2={hoverY} />
                  <circle cx={hoverX} cy={hoverY} r="3" fill={displayMeta.color} className="gpdf-crosshair-dot" />
                </>
              )}
            </>
          )}
        </svg>

        {/* The underlying candidate data is identical to Flowmap. This widget
            independently applies its own threshold, then remaps the selected
            bubbles onto the Net GEX line. */}
        <div className="gpdf-bubble-layer" aria-hidden="true">
          {deltaBubbles.map((dot) => (
            <span
              key={dot.id || `${dot.t}-${Math.round(dot.dVol)}`}
              className={`gpdf-delta-bubble ${dot.climax ? 'is-climax' : ''} ${dot.deltaEstimated ? 'is-estimated' : ''} ${dot.recoveryGap ? 'is-recovery' : ''}`}
              style={{
                left: dot.cx - dot.r,
                top: dot.cy - dot.r,
                width: dot.r * 2,
                height: dot.r * 2,
                background: flowmapBubbleBackground(dot),
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
